// RequirePermission — منقول من frontend/src/components/RequirePermission.js.
// يحجب صفحة عن مستخدم بلا صلاحيّة، مع مهلة إقلاع 4 ثوانٍ (ريثما تصل الصلاحيّات من الخادم).
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { hasPagePermission, isOwnerRouteAllowed, isSystemAdminSession } from '../lib/accessControl';
import type { Permission } from '../lib/accessControl';
import { PERMISSIONS_BOOTSTRAP_KEY, PERMISSIONS_UPDATED_EVENT, readJsonStorage, readStorage } from '../lib/storage';

interface RequirePermissionProps {
	page?: string | string[];
	anyOf?: string[];
	children: ReactNode;
}

export default function RequirePermission({ page, anyOf = [], children }: RequirePermissionProps) {
	const location = useLocation();
	const [, forceUpdate] = useState(0);
	const [bootstrapping, setBootstrapping] = useState(() => readStorage(PERMISSIONS_BOOTSTRAP_KEY) === '1');

	useEffect(() => {
		const onPermissionsUpdated = () => {
			setBootstrapping(false);
			forceUpdate((value) => value + 1);
		};
		const timer = window.setTimeout(() => setBootstrapping(false), 4000);
		window.addEventListener(PERMISSIONS_UPDATED_EVENT, onPermissionsUpdated);
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener(PERMISSIONS_UPDATED_EVENT, onPermissionsUpdated);
		};
	}, []);

	const pageKeys = (Array.isArray(page) ? page : [page]).filter((v): v is string => Boolean(v));
	const rawRequired =
		pageKeys.length === 0 && anyOf.length === 0 ? [location.pathname] : [...pageKeys, ...anyOf];

	const perms = readJsonStorage<Permission[]>('permissions', []);

	const allowed =
		isSystemAdminSession() ||
		isOwnerRouteAllowed(location.pathname) ||
		hasPagePermission(perms, rawRequired, location.pathname);

	const pending = !allowed && Boolean(readStorage('role_id')) && (bootstrapping || readStorage(PERMISSIONS_BOOTSTRAP_KEY) === '1');

	if (pending) return null;

	return allowed ? <>{children}</> : <Navigate to="/no-access" replace state={{ from: location.pathname }} />;
}
